/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

// Who can administer teams, from the backend gate config (m42.teamAdmin).
export type TeamAdminAccess = {
  allowed: boolean | undefined; // undefined while loading
  groups: string[];
  users: string[];
};

// Module-level cache so the Sidebar probe and the page probe share one
// round-trip per session instead of each hitting /access independently.
let cached: TeamAdminAccess | undefined;
let inflight: Promise<TeamAdminAccess> | undefined;

/**
 * Probes team-admin `/access` (the backend gate: m42.teamAdmin groups or
 * users). Used to gate the Sidebar entry and the page, and to show who has
 * access. `allowed` is undefined while loading; `groups`/`users` populate from
 * the gate config on an allow.
 */
export const useTeamAdminAccess = (): TeamAdminAccess => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<TeamAdminAccess>(
    cached ?? { allowed: undefined, groups: [], users: [] },
  );

  useEffect(() => {
    if (cached !== undefined) {
      setState(cached);
      return undefined;
    }
    let active = true;
    inflight =
      inflight ??
      (async () => {
        try {
          const base = await discoveryApi.getBaseUrl('team-admin');
          const res = await fetchApi.fetch(`${base}/access`);
          if (!res.ok) return { allowed: false, groups: [], users: [] };
          const json = await res.json().catch(() => ({}));
          return {
            allowed: true,
            groups: Array.isArray(json.groups) ? json.groups : [],
            users: Array.isArray(json.users) ? json.users : [],
          };
        } catch {
          return { allowed: false, groups: [], users: [] };
        }
      })();
    inflight.then(result => {
      // Only cache an allow. A denied/failed probe (network blip, cold
      // backend) stays uncached so the next mount re-probes, instead of
      // hiding the page for the rest of the session on a transient miss.
      if (result.allowed) cached = result;
      else inflight = undefined;
      if (active) setState(result);
    });
    return () => {
      active = false;
    };
  }, [discoveryApi, fetchApi]);

  return state;
};
